import { getHistory } from '@api/cut';
import { AppRoutes } from '@consts/routes';
import { useFetch } from '@hooks/useFetch';
import { useQuery } from '@hooks/useQuery';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router';
import { BasePage } from './bases/BasePage';
import { BreadcrumbSubBar } from '@components/BreadcrumbSubBar';
import SearchOffIcon from '@mui/icons-material/SearchOff';
import { Box, Typography } from '@mui/material';
import { useAlert } from '@hooks/useAlert';
import { CutTimeline } from '@components/CutHistory/CutTimeline';
import { useLoading } from '@hooks/useLoading';
import { AppShortcut, CloudSync, SwapHoriz } from '@mui/icons-material';

import type { RootState } from '@features/redux/store';
import type { ComponentProps } from 'react';

export const CutHistory = () => {
    const { IdToken } = useSelector((state: RootState) => state.auth);
    const navigate = useNavigate();
    const params = useQuery();
    const { setAlert } = useAlert();
    const { anyLoading } = useLoading();

    const CutID = params.get('id');
    if (!CutID) {
        setAlert('No Cut ID provided!', 'error');
        navigate(AppRoutes.Activity);
    }

    const { data } = useFetch({
        defaultValue: { items: [], cutId: '', status: 'Unknown' },
        fetcher: () => {
            return getHistory({
                token: IdToken!,
                params: {
                    CutID,
                },
            });
        },
        modifier: (history) => {
            if (!history.length) return { items: [], cutId: '', status: 'Unknown' };

            return {
                status: history[0].status || 'Unknown',
                cutId: history[0].cutId,
                items: [
                    {
                        content: history[0].startedDate,
                        label: 'Cut made',
                        description: 'The cut was performed.',
                        icon: <AppShortcut />,
                        color: 'info' as const,
                    },
                    {
                        content: history[0].createdDate,
                        label: 'Cut synced',
                        description: 'Data about this cut was synced to the cloud.',
                        icon: <CloudSync />,
                        color: 'success' as const,
                    },
                    ...history.reduce((acc, { eventDate, oldStatus, newStatus }) => {
                        if (!eventDate) return acc;

                        acc.push({
                            content: eventDate,
                            label: 'Status change',
                            description: (
                                <Typography>
                                    <i>{`"${oldStatus || 'Unknown'}"`}</i> to <i>{`"${newStatus || 'Unknown'}"`}</i>.
                                </Typography>
                            ),
                            icon: <SwapHoriz />,
                            color: 'secondary' as const,
                        });

                        return acc;
                    }, [] as ComponentProps<typeof CutTimeline>['items']),
                ],
            };
        },
    });

    return (
        <BasePage>
            <BreadcrumbSubBar />
            <div style={{ marginTop: '180px' }}>
                <Box display='flex' flexDirection='column' justifyContent='center' alignItems='center' gap='10px'>
                    <Typography component='h1' fontWeight='bold' fontSize='24px'>
                        Cut History{data.cutId ? ` for #${data.cutId}` : ''}
                    </Typography>

                    {!anyLoading && !data.items.length && (
                        <>
                            <SearchOffIcon sx={{ fontSize: '100px', color: '#696b6e' }} />
                            <Typography fontWeight='bold'>No history is currently available for this cut!</Typography>
                        </>
                    )}
                    {!anyLoading && data.items.length && (
                        <>
                            <Typography>
                                Current status: <b>{data.status}</b>
                            </Typography>
                            <CutTimeline items={data.items} />
                        </>
                    )}
                </Box>
            </div>
        </BasePage>
    );
};
